import Modal from '@components/primitives/modal/Modal';
import { ComponentType, FC, lazy, LazyExoticComponent, Suspense, useMemo } from 'react';
import useModals from '@components/modals/hooks/useModals';
import { Modal as IModal, ModalType } from '@models/modals/Modals';
import Throbber from '@components/primitives/throbber/Throbber';
import lazyRetry, { LazyRetryNames } from '@utils/lazyRetry';

const ApplicationModals: FC = () => {
    const { modals } = useModals();

    const modalComponentMap: {
        [K in ModalType]: LazyExoticComponent<ComponentType<IModal<K>>>;
    } = useMemo(() => {
        return {
            [ModalType.AddPagesToDocument]: lazy(() =>
                lazyRetry(() => import('@components/modals/addPagesToDocumentModal/AddPagesToDocumentModal'), LazyRetryNames.ModalAddPagesToDocument),
            ),
            [ModalType.Administrations]: lazy(() => lazyRetry(() => import('@components/modals/administrationsModal/AdministrationsModal'), LazyRetryNames.ModalAdministrations)),
            [ModalType.Annotation]: lazy(() => lazyRetry(() => import('@components/modals/annotationModal/AnnotationModal'), LazyRetryNames.ModalAnnotation)),
            [ModalType.CodePreview]: lazy(() => lazyRetry(() => import('@components/modals/codePreviewModal/CodePreviewModal'), LazyRetryNames.ModalCodePreview)),
            [ModalType.CodingAccruals]: lazy(() => lazyRetry(() => import('@components/modals/codingAccrualsModal/CodingAccrualsModal'), LazyRetryNames.ModalCodingAccruals)),
            [ModalType.Confirm]: lazy(() => lazyRetry(() => import('@components/modals/confirmModal/ConfirmModal'), LazyRetryNames.ModalConfirm)),
            [ModalType.CopyCoding]: lazy(() => lazyRetry(() => import('@components/modals/copyCodingModal/CopyCodingModal'), LazyRetryNames.ModalCopyCoding)),
            [ModalType.CreditorValidation]: lazy(() =>
                lazyRetry(() => import('@components/modals/creditorValidationModal/CreditorValidationModal'), LazyRetryNames.ModalCreditorValidation),
            ),
            [ModalType.DeleteOcrTraining]: lazy(() =>
                lazyRetry(() => import('@components/modals/deleteOcrTrainingModal/DeleteOcrTrainingModal'), LazyRetryNames.ModalDeleteOcrTraining),
            ),
            [ModalType.DocumentAttachments]: lazy(() =>
                lazyRetry(() => import('@components/modals/documentAttachmentsModal/DocumentAttachmentsModal'), LazyRetryNames.ModalDocumentAttachments),
            ),
            [ModalType.DocumentAudit]: lazy(() => lazyRetry(() => import('@components/modals/documentAuditModal/DocumentAuditModal'), LazyRetryNames.ModalDocumentAudit)),
            [ModalType.DocumentPreview]: lazy(() => lazyRetry(() => import('@components/modals/previewModal/DocumentPreviewModal'), LazyRetryNames.ModalDocumentPreview)),
            [ModalType.DocumentValidation]: lazy(() =>
                lazyRetry(() => import('@components/modals/documentValidationModal/DocumentValidationModal'), LazyRetryNames.ModalDocumentValidation),
            ),
            [ModalType.DynamicAssign]: lazy(() => lazyRetry(() => import('@components/modals/dynamicAssignModal/DynamicAssignModal'), LazyRetryNames.ModalDynamicAssign)),
            [ModalType.EditDocument]: lazy(() => lazyRetry(() => import('@components/modals/editDocumentModal/EditDocumentModal'), LazyRetryNames.ModalEditDocument)),
            [ModalType.EmailDocument]: lazy(() => lazyRetry(() => import('@components/modals/emailDocumentModal/EmailDocumentModal'), LazyRetryNames.ModalEmailDocument)),
            [ModalType.EmailPreview]: lazy(() => lazyRetry(() => import('@components/modals/emailPreviewModal/EmailPreviewModal'), LazyRetryNames.ModalEmailPreview)),
            [ModalType.GridSettings]: lazy(() => lazyRetry(() => import('@components/modals/gridSettingsModal/GridSettingsModal'), LazyRetryNames.ModalGridSettings)),
            [ModalType.LinkDocuments]: lazy(() => lazyRetry(() => import('@components/modals/linkDocumentsModal/LinkDocumentsModal'), LazyRetryNames.ModalLinkDocuments)),
            [ModalType.List]: lazy(() => lazyRetry(() => import('@components/modals/listModal/ListModal'), LazyRetryNames.ModalList)),
            [ModalType.OcrCodingTraining]: lazy(() =>
                lazyRetry(() => import('@components/modals/ocrCodingTrainingModal/OcrCodingTrainingModal'), LazyRetryNames.ModalOcrCodingTraining),
            ),
            [ModalType.OcrTrainingField]: lazy(() =>
                lazyRetry(() => import('@components/modals/ocrTrainingFieldModal/OcrTrainingFieldModal'), LazyRetryNames.ModalOcrTrainingField),
            ),
            [ModalType.OcrTrainingLog]: lazy(() => lazyRetry(() => import('@components/modals/ocrTrainingLogModal/OcrTrainingLogModal'), LazyRetryNames.ModalOcrTrainingLog)),
            [ModalType.OcrTrainingSql]: lazy(() => lazyRetry(() => import('@components/modals/ocrTrainingSqlModal/OcrTrainingSqlModal'), LazyRetryNames.ModalOcrTrainingSql)),
            [ModalType.PerformDocumentAction]: lazy(() =>
                lazyRetry(() => import('@components/modals/performDocumentActionModal/PerformDocumentActionModal'), LazyRetryNames.ModalPerformDocumentAction),
            ),
            [ModalType.RefreshLookup]: lazy(() => lazyRetry(() => import('@components/modals/refreshLookupModal/RefreshLookupModal'), LazyRetryNames.ModalRefreshLookup)),
            [ModalType.RepairDocument]: lazy(() => lazyRetry(() => import('@components/modals/repairDocumentModal/RepairDocumentModal'), LazyRetryNames.ModalRepairDocument)),
            [ModalType.Replacement]: lazy(() => lazyRetry(() => import('@components/modals/replacementModal/ReplacementModal'), LazyRetryNames.ModalReplacement)),
            [ModalType.SamFeedback]: lazy(() => lazyRetry(() => import('@components/modals/samFeedbackModal/SamFeedbackModal'), LazyRetryNames.ModalSamFeedback)),
            [ModalType.SamLineSource]: lazy(() => lazyRetry(() => import('@components/modals/samLineSourceModal/SamLineSourceModal'), LazyRetryNames.ModalSamLineSource)),
            [ModalType.SamLineSourceMore]: lazy(() =>
                lazyRetry(() => import('@components/modals/samLineSourceMoreModal/SamLineSourceMoreModal'), LazyRetryNames.ModalSamLineSourceMore),
            ),
            [ModalType.SamIntroduction]: lazy(() => lazyRetry(() => import('@components/modals/samIntroductionModal/SamIntroductionModal'), LazyRetryNames.ModalSamIntroduction)),
            [ModalType.SamBackOfficeStatusHelper]: lazy(() =>
                lazyRetry(() => import('@components/modals/samBackOfficeStatusHelperModal/SamBackOfficeStatusHelperModal'), LazyRetryNames.ModalSamBackOfficeStatusHelper),
            ),
            [ModalType.TemplateSharingManagement]: lazy(() =>
                lazyRetry(() => import('@components/modals/templateSharingManagementModal/TemplateSharingManagementModal'), LazyRetryNames.ModalTemplateSharingManagement),
            ),
            [ModalType.Text]: lazy(() => lazyRetry(() => import('@components/modals/textModal/TextModal'), LazyRetryNames.ModalText)),
            [ModalType.UserReplaced]: lazy(() => lazyRetry(() => import('@components/modals/userReplacedModal/UserReplacedModal'), LazyRetryNames.ModalUserReplaced)),
            [ModalType.WorkflowActionValidation]: lazy(() =>
                lazyRetry(() => import('@components/modals/workflowActionValidationModal/WorkflowActionValidationModal'), LazyRetryNames.ModalWorkflowActionValidation),
            ),
        };
    }, []);

    return (
        <>
            {modals.map((modal) => {
                const ModalComponent = modalComponentMap[modal.type] as React.ComponentType<typeof modal>;
                if (!ModalComponent) {
                    console.warn(`Modal component ${modal.type} not found`);
                    return null;
                }

                return (
                    <Modal key={modal.id} {...modal}>
                        <Suspense
                            fallback={
                                <div className="modal__content">
                                    <Throbber />
                                </div>
                            }
                        >
                            <ModalComponent {...modal} />
                        </Suspense>
                    </Modal>
                );
            })}
        </>
    );
};

export default ApplicationModals;
